import type { DeepPartial, PlayerSettings } from '@cartwhl/3d/player';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Select from '@cartwhl/ui/atoms/Select';
import Slider from '@cartwhl/ui/atoms/Slider';
import ToggleGroup from '@cartwhl/ui/atoms/ToggleGroup';
import Text from '@cartwhl/ui/base/Text';
import BackgroundIcon from '@cartwhl/ui/icons/BackgroundIcon';
import GradientIcon from '@cartwhl/ui/icons/GradientIcon';
import ColorPicker from '@cartwhl/ui/molecules/ColorPicker';
import PanelSection from '@cartwhl/ui/molecules/PanelSection';

import type { KeyPoseGhostMaterialVariant } from '../../keyPoses';
import {
  isKeyPoseGhostMaterialVariant,
  KEY_POSE_GHOST_MATERIAL_VARIANT_OPTIONS,
} from '../../keyPoses/keyPoseGhostMaterialVariants';

const gradientLabel = 'gradient';
const solidLabel = 'solid';

const BACKGROUND_OPTIONS = [
  {
    label: gradientLabel,
    value: gradientLabel,
    content: <GradientIcon />,
  },
  {
    label: solidLabel,
    value: solidLabel,
    content: <BackgroundIcon />,
  },
];

const GRID_SPACING_MIN = 0.05;
const GRID_SPACING_MAX = 2;
const GRID_SPACING_STEP = 0.01;

interface StageOptionsPanelProps {
  environment: PlayerSettings['environment'];
  keyPoseGhostMaterialVariant: KeyPoseGhostMaterialVariant;
  showActorNameTags: boolean;
  showActorNameTagsOption: boolean;
  showKeyPoseGhostMaterialVariantOption: boolean;
  loopPlayback: boolean;
  onEnvironmentChange: (
    nextEnvironment: DeepPartial<PlayerSettings['environment']>,
  ) => void;
  onKeyPoseGhostMaterialVariantChange: (
    variant: KeyPoseGhostMaterialVariant,
  ) => void;
  onShowActorNameTagsChange: (checked: boolean) => void;
  onLoopPlaybackChange: (checked: boolean) => void;
}

export default function StageOptionsPanel({
  environment: {
    useBackgroundGradient,
    backdropHexColor,
    floor,
    showGrid,
    gridCellSize,
  },
  keyPoseGhostMaterialVariant,
  showActorNameTags,
  showActorNameTagsOption,
  showKeyPoseGhostMaterialVariantOption,
  loopPlayback,
  onEnvironmentChange,
  onKeyPoseGhostMaterialVariantChange,
  onShowActorNameTagsChange,
  onLoopPlaybackChange,
}: StageOptionsPanelProps) {
  const handleGridCellSizeChange = (value: number) => {
    const clampedValue = Math.min(
      GRID_SPACING_MAX,
      Math.max(GRID_SPACING_MIN, value),
    );
    onEnvironmentChange({
      gridCellSize: Number(clampedValue.toFixed(2)),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <PanelSection
        title="Stage"
        content={[
          {
            title: 'View options',
            children: (
              <div className="flex flex-col gap-4">
                <div className="grid auto-rows-[34px] grid-cols-2 items-center gap-2">
                  <Checkbox
                    id="motion-editor-stage-shadows"
                    variant="checkbox"
                    label="Shadows"
                    color="settings"
                    checked={floor === 'shadow'}
                    setChecked={(checked) =>
                      onEnvironmentChange({
                        floor: checked ? 'shadow' : 'none',
                      })
                    }
                  />
                  <Checkbox
                    id="motion-editor-stage-ground-plane"
                    variant="checkbox"
                    label="Ground plane"
                    color="settings"
                    checked={floor === 'ground'}
                    setChecked={(checked) =>
                      onEnvironmentChange({
                        floor: checked ? 'ground' : 'none',
                      })
                    }
                  />
                  <Checkbox
                    id="motion-editor-stage-grid"
                    variant="checkbox"
                    label="Grid"
                    color="settings"
                    checked={showGrid}
                    setChecked={(checked) =>
                      onEnvironmentChange({
                        showGrid: checked,
                      })
                    }
                  />
                  {showActorNameTagsOption ? (
                    <Checkbox
                      id="motion-editor-stage-name-tags"
                      variant="checkbox"
                      label="Name tags"
                      color="settings"
                      checked={showActorNameTags}
                      setChecked={onShowActorNameTagsChange}
                    />
                  ) : null}
                </div>

                {showKeyPoseGhostMaterialVariantOption ? (
                  <div className="flex flex-col gap-2">
                    <Text
                      variant="studio/body/medium/sm"
                      className="text-secondary"
                    >
                      Key pose ghost style
                    </Text>
                    <Select
                      items={[...KEY_POSE_GHOST_MATERIAL_VARIANT_OPTIONS]}
                      value={keyPoseGhostMaterialVariant}
                      onValueChange={(value) => {
                        if (isKeyPoseGhostMaterialVariant(value)) {
                          onKeyPoseGhostMaterialVariantChange(value);
                        }
                      }}
                    />
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-secondary"
                  >
                    Grid spacing
                  </Text>
                  <div className="flex items-center gap-4">
                    <Slider
                      rangeColor="blue"
                      min={GRID_SPACING_MIN}
                      max={GRID_SPACING_MAX}
                      step={GRID_SPACING_STEP}
                      currentValue={gridCellSize}
                      onChange={handleGridCellSizeChange}
                    />
                    <Text
                      variant="studio/body/medium/xs"
                      className="bg-gray-15 dark:bg-gray-75 flex h-6 w-12 items-center justify-center rounded-lg tracking-tighter tabular-nums"
                    >
                      {gridCellSize.toFixed(2)}x
                    </Text>
                  </div>
                </div>
              </div>
            ),
          },
          {
            title: 'Playback',
            children: (
              <Checkbox
                id="motion-editor-stage-loop-playback"
                variant="checkbox"
                label="Loop playback"
                color="settings"
                checked={loopPlayback}
                setChecked={onLoopPlaybackChange}
              />
            ),
          },
          {
            title: 'Background',
            children: (
              <div>
                <ToggleGroup
                  className="mb-4"
                  items={BACKGROUND_OPTIONS}
                  value={
                    useBackgroundGradient === true ? gradientLabel : solidLabel
                  }
                  onValueChange={(value) =>
                    onEnvironmentChange({
                      useBackgroundGradient: value === gradientLabel,
                    })
                  }
                />
                <ColorPicker
                  value={backdropHexColor}
                  onChange={(value) =>
                    onEnvironmentChange({
                      backdropHexColor: value,
                    })
                  }
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
